import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons/faQuestionCircle';
import { faTimes as faTimesLight } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faBell } from '@fortawesome/pro-solid-svg-icons/faBell';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { emailValuationSchema } from '@propertypal/shared/src/constants/instantValuation';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { parseApiDate } from '@propertypal/shared/src/services/dayjs';
import { ValuationResult } from '@propertypal/shared/src/types/valuations';
import Button from '@propertypal/web-ui/src/buttons/Button';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import ModalWrapper from '@propertypal/web-ui/src/modal/ModalWrapper';
import { useToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { SubHeading, MidHeading, Text, UnorderedList } from '@propertypal/web-ui/src/typography';
import AgentsBox from '@propertypal/web-ui/src/valuations/AgentsBox';
import ListingBox from '@propertypal/web-ui/src/valuations/ListingBox';
import ValuationRange from '@propertypal/web-ui/src/valuations/ValuationRange';
import { useFormik } from 'formik';
import React, { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import {
  ConfidenceBox,
  ModalBackground,
  ModalContent,
  ResultBox,
  IconWrapper,
  Heading,
  List,
  BottomListWrapper,
  BottomList,
  ModalContentWrapper,
  LongText,
  Li,
  Line,
  EmailWrapper,
} from './InstantValuationResult.style';

interface Props {
  result: ValuationResult;
  onContactClick: () => void;
  fetchResult: (path: string) => void;
}

const InstantValuationResult: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const agents = props.result.promotedAgents?.map(({ account }) => account);
  const [showConfidenceModal, setShowConfidenceModal] = useState(false);
  const [showEstimatedValueModal, setShowEstimatedValueModal] = useState(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const dateRequested = parseApiDate(props.result.timeRequested).format('DD MMM YYYY');
  const { openToast } = useToast();

  const submitEmail = async (data: any) => {
    setLoading(true);

    try {
      await axios(
        {
          method: 'PUT',
          url: `${API_URL}/valuation/${props.result?.id}/email`,
          data,
        },
        dispatch,
      );

      trackGaEvent('valuation_register_email', {});

      await props.fetchResult(`/valuation/${props.result.id}`);
    } catch {
      setLoading(false);
      openToast({
        title: 'Error',
        message: 'Something went wrong. Please try again later.',
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: emailValuationSchema,
    onSubmit: (values) => {
      if (!loading) {
        submitEmail({
          email: values.email,
        });
      }
    },
  });

  const renderContactInfo = () => (
    <>
      {props.result.avmResult.minimum || props.result.avmResult.maximum ? (
        <Text fontSize={18} align="center" color={theme.primary} mb={25}>
          Don’t forget, this is only a computer estimate.
        </Text>
      ) : null}

      {!props.result.emailProvided && (
        <>
          {!props.result.avmResult.minimum || !props.result.avmResult.maximum ? (
            <div style={{ display: 'flex' }}>
              <FontAwesomeIcon icon={faBell} color={theme.textDark} style={{ fontSize: 20, marginTop: '1px' }} />
              <Text fontWeight="bold" style={{ marginLeft: '18px' }}>
                We can&apos;t provide a valuation for this property right now{' '}
                <span style={{ fontWeight: 'normal' }}>
                  but pop in your email address below and we&apos;ll keep you posted when we can.
                </span>
              </Text>
            </div>
          ) : (
            <div style={{ display: 'flex' }}>
              <FontAwesomeIcon icon={faBell} color={theme.textDark} style={{ fontSize: 20, marginTop: '1px' }} />
              <div>
                <Text fontWeight="bold" style={{ marginLeft: '18px' }}>
                  Want to keep up-to-date when the valuation changes?{' '}
                  <span style={{ fontWeight: 'normal' }}>Pop in your email address below.</span>
                </Text>
              </div>
            </div>
          )}
          <form onSubmit={formik.handleSubmit}>
            <EmailWrapper>
              <TextInput
                name="email"
                containerStyle={{ maxWidth: '70%', marginRight: '10px' }}
                placeholder="Enter email address"
                testID="emailInput"
                value={formik.values.email}
                onValueChange={formik.handleChange('email')}
                error={formik.touched.email && formik.errors.email}
              />
              <Button
                containerStyle={{ maxWidth: '30%', height: '56px' }}
                loading={loading}
                testID="submitEmailButton"
                type="submit"
              >
                Submit
              </Button>
            </EmailWrapper>
          </form>
        </>
      )}

      {props.result.emailProvided && (
        <>
          <Text fontWeight="bold" align="center">
            Thank you
          </Text>
          <Text align="center" style={{ marginBottom: '25px' }}>
            We&apos;ll be in touch when the valuation changes.
          </Text>
        </>
      )}

      <Line />

      <MidHeading mt={25} align="center" fontSize={18}>
        {!props.result.avmResult.minimum || !props.result.avmResult.maximum
          ? 'Want a valuation from a professional?'
          : 'Ready for a more accurate valuation?'}
      </MidHeading>

      <Text mt={10} align="center">
        Select and contact expert estate agents to arrange an in-person valuation of your home.
      </Text>

      <Button containerStyle={{ marginTop: 20 }} onClick={props.onContactClick}>
        Contact expert agents today
      </Button>

      <MidHeading mt={30} fontSize={16} align="center">
        Some of the property experts active in your area
      </MidHeading>

      <AgentsBox agents={agents} align="center" />

      <ModalWrapper show={showConfidenceModal} onClose={() => setShowConfidenceModal(false)}>
        <ModalBackground>
          <ModalContent>
            <button
              onClick={() => setShowConfidenceModal(false)}
              style={{ alignSelf: 'flex-end', position: 'absolute' }}
              type="button"
              data-testid="hideMoreInformation"
            >
              <FontAwesomeIcon icon={faTimes} color={theme.textDark} style={{ fontSize: 22 }} />
            </button>

            <ConfidenceBox>
              <Text fontSize={14}>HIGH CONFIDENCE</Text>
              <FontAwesomeIcon icon={faQuestionCircle} color={theme.textDark} style={{ marginLeft: 5 }} />
            </ConfidenceBox>

            <Text mt={10} mb={20} fontSize={14}>
              This rating means the property closely resembles other homes in the area, providing a reliable foundation
              for the estimate. Despite limited data in our models, the available information is current and the market
              is stable. You can expect a fairly accurate estimate of the property&apos;s value.
            </Text>

            <ConfidenceBox>
              <Text fontSize={14}>MEDIUM CONFIDENCE</Text>
              <FontAwesomeIcon icon={faQuestionCircle} color={theme.textDark} style={{ marginLeft: 5 }} />
            </ConfidenceBox>

            <Text mt={10} mb={20} fontSize={14}>
              This rating indicates that there is some data for an estimate, but fewer similar properties to compare to
              than high confidence properties. The restricted data in our models lowers the confidence in our estimate.
              The value range will be broader than high confidence estimates but narrower than low confidence ones,
              resulting in a somewhat accurate but less precise estimate.
            </Text>

            <ConfidenceBox>
              <Text fontSize={14}>LOW CONFIDENCE</Text>
              <FontAwesomeIcon icon={faQuestionCircle} color={theme.textDark} style={{ marginLeft: 5 }} />
            </ConfidenceBox>

            <Text mt={10} fontSize={14}>
              This rating suggests the property may have unique features or characteristics, making it challenging to
              find sufficient data for an estimate. The limited available data on similar properties and possible
              outdated or fluctuating market conditions reduce the confidence in the estimate. The value range for low
              confidence properties is less reliable, reflecting greater uncertainty about the property&apos;s value due
              to the more limited data.
            </Text>
          </ModalContent>
        </ModalBackground>
      </ModalWrapper>
    </>
  );

  if (props.result.listing) {
    return (
      <>
        <SubHeading fontSize={28}>Instant Valuation</SubHeading>

        {props.result.listing.published && (
          <Text fontSize={18} mt={15} color={theme.primary}>
            PropertyPal&apos;s computer says this property is already on the market.
          </Text>
        )}

        {!props.result.listing.published && (
          <>
            <Text fontSize={18} mt={15} color={theme.primary}>
              PropertyPal&apos;s computer says this property has recently been on the market in the last six months.
            </Text>

            <Text fontSize={18} mt={15} color={theme.primary}>
              Please contact the agent directly if you want to find out more about this property.
            </Text>
          </>
        )}

        <ListingBox property={props.result.listing} />
      </>
    );
  }

  if (!props.result.avmResult.minimum || !props.result.avmResult.maximum) {
    return (
      <>
        <SubHeading fontSize={28} align="center">
          Oh No!
        </SubHeading>

        <Text fontSize={18} mt={15} align="center" color={theme.primary}>
          PropertyPal&apos;s computer says no! We&apos;re sorry but it can&apos;t provide a valuation for this property.
        </Text>

        <ResultBox>
          <Text fontWeight="bold" align="center">
            This may be for a variety of reasons:
          </Text>

          <UnorderedList style={{ padding: '0 30px 0 50px' }}>
            <li>Property is in an area with low housing density</li>
            <li>Property is very unique</li>
            <li>Property has an extreme value</li>
          </UnorderedList>
        </ResultBox>

        {renderContactInfo()}
      </>
    );
  }

  const renderEstimatedValueModal = () => (
    <ModalWrapper show={showEstimatedValueModal} onClose={() => setShowEstimatedValueModal(false)}>
      <ModalBackground>
        <ModalContent>
          <button
            onClick={() => setShowEstimatedValueModal(false)}
            style={{ alignSelf: 'flex-end', position: 'relative', top: -5, left: 10 }}
            type="button"
            data-testid="hideMoreInformation"
          >
            <FontAwesomeIcon icon={faTimesLight} color={theme.textLight} style={{ fontSize: 26 }} />
          </button>
          <Heading>How we calculate our estimates</Heading>
          <ModalContentWrapper>
            <Text mt={10} mb={20}>
              Using extensive market data, our sophisticated computer algorithm will provide you with an estimated value
              range of what the property could be worth.
            </Text>
            <div>
              <Text fontWeight="bold">The computer includes data from:</Text>
              <List>
                <li>
                  <FontAwesomeIcon icon={faCheck} color={theme.linkGreen} style={{ fontSize: 20, marginRight: 10 }} />
                  Property listings on PropertyPal
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} color={theme.linkGreen} style={{ fontSize: 20, marginRight: 10 }} />
                  Address information from Royal Mail
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} color={theme.linkGreen} style={{ fontSize: 20, marginRight: 10 }} />
                  Data from Land and Property Services
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} color={theme.linkGreen} style={{ fontSize: 20, marginRight: 10 }} />
                  Energy Performance Certificates
                </li>
              </List>
            </div>
            <BottomListWrapper>
              <Text fontWeight="bold">The computer will not include:</Text>
              <BottomList>
                <Li>
                  <FontAwesomeIcon icon={faTimes} color={theme.primary} style={{ fontSize: 20, marginRight: 15 }} />
                  Extensive remodelling or renovation work
                </Li>
                <Li>
                  <FontAwesomeIcon icon={faTimes} color={theme.primary} style={{ fontSize: 20, marginRight: 15 }} />
                  <LongText>
                    Unique features such as swimming pools, listed buildings, large plots, triple garages, outbuildings,
                    annexes or scenic views
                  </LongText>
                </Li>
                <Li>
                  <FontAwesomeIcon icon={faTimes} color={theme.primary} style={{ fontSize: 20, marginRight: 15 }} />
                  The property condition
                </Li>
                <Li>
                  <FontAwesomeIcon icon={faTimes} color={theme.primary} style={{ fontSize: 20, marginRight: 15 }} />
                  New-build properties
                </Li>
                <Li>
                  <FontAwesomeIcon icon={faTimes} color={theme.primary} style={{ fontSize: 20, marginRight: 15 }} />
                  <LongText>
                    Green energy improvements such as solar panels, heat recovery systems and battery storage packs
                  </LongText>
                </Li>
              </BottomList>
            </BottomListWrapper>
            <Text mt={20} mb={20} fontWeight="bold">
              These factors can have a significant impact on the property value and will not be captured by Instant
              Valuation. For an accurate property valuation, always speak to an expert estate agent.
            </Text>
          </ModalContentWrapper>
        </ModalContent>
      </ModalBackground>
    </ModalWrapper>
  );

  return (
    <>
      <SubHeading fontSize={28} align="center">
        Ta-Da!
      </SubHeading>

      <Text fontSize={18} mt={15} align="center" color={theme.primary}>
        PropertyPal&apos;s computer says...
      </Text>

      <ResultBox>
        <SubHeading
          align="center"
          fontSize={18}
          style={{ display: 'flex', alignItems: 'center', marginBottom: '50px' }}
        >
          Estimated value range
          <IconWrapper
            onClick={() => setShowEstimatedValueModal(true)}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <FontAwesomeIcon icon={faInfoCircle} color={theme.primary} style={{ marginLeft: 5, fontSize: 20 }} />
          </IconWrapper>
        </SubHeading>

        <ValuationRange
          lowerEstimate={props.result.avmResult.minimum}
          higherEstimate={props.result.avmResult.maximum}
          middleEstimate={props.result.avmResult.middle}
        />

        <Text fontSize={11} align="center" mt={40} mb={10} style={{ fontStyle: 'italic' }}>
          Valuation last updated: {dateRequested}
        </Text>

        <ConfidenceBox>
          <Text fontSize={14}>{props.result.avmResult.confidence} CONFIDENCE</Text>

          <button
            aria-label="More Information"
            onClick={() => setShowConfidenceModal(true)}
            type="button"
            data-testid="moreInformation"
          >
            <FontAwesomeIcon icon={faQuestionCircle} color={theme.textDark} style={{ marginLeft: 5 }} />
          </button>
        </ConfidenceBox>
      </ResultBox>

      {renderContactInfo()}

      {renderEstimatedValueModal()}
    </>
  );
};

export default InstantValuationResult;
